import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── ScortaCilindriModal ──────────────────────────────────────────────────────
const ScortaCilindriModal = ({ isOpen, onClose }) => {

  const [isLoading, setIsLoading]       = useState(false);
  const [inRiparazione, setInRiparazione] = useState([]);
  const [pronti, setPronti]             = useState([]);
  const [lastRefresh, setLastRefresh]   = useState(null);


  // ── Fetch — TUTTI i record con stato PRONTO o IN RIPARAZIONE ─────────────
  const fetchScorta = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, stato, dataintervento, colata, linea, carropaniera')
        .not('ncilindro', 'is', null)
        .in('stato', ['PRONTO', 'IN RIPARAZIONE'])
        .order('dataintervento', { ascending: false });

      if (error) throw error;
      if (!data) return;

      const rip = data
        .filter(r => r.stato?.toUpperCase() === 'IN RIPARAZIONE')
        .sort((a, b) => a.ncilindro.localeCompare(b.ncilindro, undefined, { numeric: true }));

      const prt = data
        .filter(r => r.stato?.toUpperCase() === 'PRONTO')
        .sort((a, b) => a.ncilindro.localeCompare(b.ncilindro, undefined, { numeric: true }));

      setInRiparazione(rip);
      setPronti(prt);
      setLastRefresh(new Date());

    } catch (err) {
      console.error('ScortaCilindri fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };


  // ── Fetch on open ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      fetchScorta();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);


  // ── Escape key ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen]);


  // ── Format date — solo data, senza ora ───────────────────────────────────
  const formatDate = (ts) => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleDateString('it-IT', {
        day:   '2-digit',
        month: '2-digit',
        year:  'numeric',
      });
    } catch {
      return '—';
    }
  };

  // ── GENERATE PDF REPORT ──────────────────────────────────────────────────
const generatePDF = () => {
    const doc = new jsPDF();
    const now = new Date().toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    // --- HEADER ---
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text('OrizonDesk - Sistema Gestione Inventario', 14, 10);
    
    doc.setFontSize(22);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORT SCORTA CILINDRI', 14, 22);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100);
    doc.text(`Report del giorno: ${now}`, 14, 28);
    
    doc.setDrawColor(37, 99, 235); // Plava linija (primary color)
    doc.setLineWidth(0.5);
    doc.line(14, 32, 196, 32);

    // --- SEKCIJA 1: PRONTI ---
    doc.setFontSize(15);
    doc.setTextColor(16, 185, 129); // Emerald
    doc.setFont('helvetica', 'bold'); // Deblji font za naslov
    doc.text(`CILINDRI PRONTI (${pronti.length})`, 14, 42);

    const prontiRows = pronti.map(item => [
      item.ncilindro,
      'PRONTO',
      formatDate(item.dataintervento),
      `${item.colata || ''} ${item.linea || ''} ${item.carropaniera || ''}`.trim() || '---'
    ]);

    autoTable(doc, {
      startY: 45,
      head: [['N° CILINDRO', 'STATO', 'DATA INTERV.', 'DETTAGLI']],
      body: prontiRows.length > 0 ? prontiRows : [['---', 'Nessun cilindro', '---', '---']],
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129], fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { fontStyle: 'bold' } }
    });

    // --- SEKCIJA 2: IN RIPARAZIONE ---
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(15);
    doc.setTextColor(249, 115, 22); // Orange
    doc.setFont('helvetica', 'bold'); // Deblji font za naslov
    doc.text(`IN RIPARAZIONE (${inRiparazione.length})`, 14, finalY);

    const ripRows = inRiparazione.map(item => [
      item.ncilindro,
      'IN RIPARAZIONE',
      formatDate(item.dataintervento),
      `${item.colata || ''} ${item.linea || ''} ${item.carropaniera || ''}`.trim() || '---'
    ]);

    autoTable(doc, {
      startY: finalY + 3,
      head: [['N° CILINDRO', 'STATO', 'DATA INTERV.', 'DETTAGLI']],
      body: ripRows.length > 0 ? ripRows : [['---', 'Nessun cilindro', '---', '---']],
      theme: 'striped',
      headStyles: { fillColor: [249, 115, 22], fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { fontStyle: 'bold' } }
    });

    // --- FOOTER (PAGINACIJA I LINK) ---
    const pageCount = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Linija iznad footera
        doc.setDrawColor(230);
        doc.setLineWidth(0.1);
        doc.line(14, pageHeight - 15, pageWidth - 14, pageHeight - 15);

        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont('helvetica', 'normal');

        // Lijevo: Stampato il
        doc.text(`Stampato il: ${now}`, 14, pageHeight - 10);

        // Sredina: OrizonDesk
        doc.setFont('helvetica', 'bold');
        doc.text('OrizonDesk', pageWidth / 2, pageHeight - 10, { align: 'center' });

        // Desno: URL
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(37, 99, 235); // Plava za link
        doc.text('https://desk.orizon-aix.com', pageWidth - 14, pageHeight - 10, { align: 'right' });
        
        // Broj stranice skroz dole desno ili pored linka
        doc.setTextColor(150);
        doc.text(`Pagina ${i}/${pageCount}`, pageWidth - 14, pageHeight - 5, { align: 'right' });
    }

    doc.save(`Report_Scorta_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (!isOpen) return null;


  // ── Pill componente ───────────────────────────────────────────────────────
  const CilindroPill = ({ item, variant }) => {
    const isOrange = variant === 'riparazione';
    return (
      <div className={`
        flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border transition-all
        ${isOrange
          ? 'bg-orange-500/8 border-orange-500/20 hover:bg-orange-500/12 hover:border-orange-500/35'
          : 'bg-emerald-500/8 border-emerald-500/20 hover:bg-emerald-500/12 hover:border-emerald-500/35'
        }
      `}>
        {/* N° cilindro */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`
            w-2 h-2 rounded-full shrink-0
            ${isOrange ? 'bg-orange-500' : 'bg-emerald-500'}
          `} />
          <span className={`
            text-sm font-black tracking-tight
            ${isOrange ? 'text-orange-700 dark:text-orange-400' : 'text-emerald-700 dark:text-emerald-400'}
          `}>
            {item.ncilindro}
          </span>
        </div>

        {/* Info destra */}
        <div className="flex items-center gap-3 shrink-0">
          {(item.colata || item.linea || item.carropaniera) && (
            <span className="hidden sm:flex items-center gap-1 text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest">
              {[item.colata, item.linea, item.carropaniera].filter(Boolean).join(' · ')}
            </span>
          )}
          <span className={`
            text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl
            ${isOrange
              ? 'bg-orange-500/15 text-orange-600 dark:text-orange-400'
              : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
            }
          `}>
            {formatDate(item.dataintervento)}
          </span>
        </div>
      </div>
    );
  };


  // ── Section componente ────────────────────────────────────────────────────
  const Section = ({ title, icon, count, items, variant, emptyMsg }) => {
    const isOrange = variant === 'riparazione';
    return (
      <div className={`
        flex flex-col rounded-3xl border overflow-hidden
        ${isOrange
          ? 'border-orange-500/25 bg-orange-500/4'
          : 'border-emerald-500/25 bg-emerald-500/4'
        }
      `}>
        {/* Header sezione */}
        <div className={`
          px-6 py-5 flex items-center justify-between border-b
          ${isOrange
            ? 'border-orange-500/20 bg-orange-500/8'
            : 'border-emerald-500/20 bg-emerald-500/8'
          }
        `}>
          <div className="flex items-center gap-3">
            <div className={`
              w-9 h-9 rounded-xl flex items-center justify-center
              ${isOrange
                ? 'bg-orange-500/20 text-orange-500'
                : 'bg-emerald-500/20 text-emerald-500'
              }
            `}>
              <Icon name={icon} size={17} />
            </div>
            <div>
              <h3 className={`
                text-sm font-black uppercase tracking-tight
                ${isOrange
                  ? 'text-orange-700 dark:text-orange-400'
                  : 'text-emerald-700 dark:text-emerald-400'
                }
              `}>
                {title}
              </h3>
              <p className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest mt-0.5">
                {count === 0
                  ? 'Nessun cilindro'
                  : count === 1
                    ? '1 cilindro'
                    : `${count} cilindri`}
              </p>
            </div>
          </div>

          {/* Badge contatore */}
          <div className={`
            min-w-[2.5rem] h-10 rounded-2xl flex items-center justify-center px-3
            text-lg font-black tabular-nums
            ${isOrange
              ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
              : 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
            }
          `}>
            {count}
          </div>
        </div>

        {/* Lista cilindri */}
        <div className="p-4 flex flex-col gap-2 max-h-72 overflow-y-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}>
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <Icon
                name={isOrange ? 'CheckCircle2' : 'PackageOpen'}
                size={28}
                className={isOrange ? 'text-emerald-500/50' : 'text-muted-foreground/20'}
              />
              <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/40">
                {emptyMsg}
              </span>
            </div>
          ) : (
            items.map((item, idx) => (
              <CilindroPill key={`${item.ncilindro}-${idx}`} item={item} variant={variant} />
            ))
          )}
        </div>
      </div>
    );
  };


  // ── Render modale ─────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 overflow-hidden">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div className="relative w-full max-w-2xl bg-card border border-border/50 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">

        <div className="absolute -top-16 -right-16 w-48 h-48 bg-emerald-500/8 blur-[60px] rounded-full pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-orange-500/8 blur-[60px] rounded-full pointer-events-none" />

        {/* ── HEADER ── */}
        <div className="shrink-0 px-7 md:px-9 py-6 flex items-center justify-between border-b border-border/40 relative z-10 bg-card/70 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-muted/50 border border-border/30 flex items-center justify-center text-foreground/60 shadow-sm">
              <Icon name="PackageSearch" size={21} />
            </div>
            <div>
              <h2 className="text-lg font-black tracking-tighter uppercase text-foreground">
                Scorta Cilindri
              </h2>
              <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mt-0.5">
                Situazione attuale — CCO
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* PDF Button */}
            <button
              onClick={generatePDF}
              disabled={isLoading || (pronti.length === 0 && inRiparazione.length === 0)}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-500/10 hover:bg-blue-500 border border-blue-500/20 text-blue-500 hover:text-white transition-all disabled:opacity-40"
              title="Scarica Report PDF"
            >
              <Icon name="FileDown" size={16} />
            </button>

            <button
              onClick={fetchScorta}
              disabled={isLoading}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted/50 hover:bg-muted border border-border/40 text-muted-foreground hover:text-foreground transition-all disabled:opacity-40"
              aria-label="Aggiorna"
            >
              <Icon name="RefreshCw" size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>

            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted/50 hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-muted-foreground transition-all group"
              aria-label="Chiudi"
            >
              <Icon name="X" size={16} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        {/* ── BODY ── */}
        <div
          className="flex-1 overflow-y-auto px-7 md:px-9 py-6 flex flex-col gap-5 relative z-10"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}
        >
          {isLoading ? (
            <div className="flex flex-col gap-5">
              {[0, 1].map(i => (
                <div key={i} className="rounded-3xl border border-border/30 overflow-hidden">
                  <div className="px-6 py-5 bg-muted/20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-muted/50 animate-pulse" />
                      <div className="flex flex-col gap-1.5">
                        <div className="w-28 h-3.5 rounded-full bg-muted/50 animate-pulse" />
                        <div className="w-20 h-2.5 rounded-full bg-muted/40 animate-pulse" />
                      </div>
                    </div>
                    <div className="w-10 h-10 rounded-2xl bg-muted/50 animate-pulse" />
                  </div>
                  <div className="p-4 flex flex-col gap-2">
                    {[0, 1, 2].map(j => (
                      <div key={j} className="h-11 rounded-2xl bg-muted/30 animate-pulse" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* ── 1. PRONTI prima — verde ── */}
              <Section
                title="Cilindri Pronti"
                icon="CheckCircle2"
                count={pronti.length}
                items={pronti}
                variant="pronto"
                emptyMsg="Nessun cilindro disponibile"
              />

              {/* ── Separator ── */}
              <hr style={{ border: 'none', borderTop: '2px solid #6b7280', margin: '4px 0', opacity: 0.35 }} />

              {/* ── 2. IN RIPARAZIONE dopo — arancione ── */}
              <Section
                title="In Riparazione"
                icon="Wrench"
                count={inRiparazione.length}
                items={inRiparazione}
                variant="riparazione"
                emptyMsg="Nessun cilindro in riparazione"
              />
            </>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="shrink-0 px-7 md:px-9 py-4 bg-muted/20 border-t border-border/40 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-2 text-muted-foreground/30">
            <Icon name="Clock" size={11} />
            <span className="text-[9px] font-black uppercase tracking-[.2em]">
              {lastRefresh
                ? `Aggiornato ${lastRefresh.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                : 'Caricamento...'}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-[10px] font-black">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">{pronti.length}</span>
              <span className="text-muted-foreground/40 uppercase tracking-widest">pronti</span>
            </span>
            <span className="flex items-center gap-1.5 text-[10px] font-black">
              <span className="w-2 h-2 rounded-full bg-orange-500" />
              <span className="text-orange-600 dark:text-orange-400 tabular-nums">{inRiparazione.length}</span>
              <span className="text-muted-foreground/40 uppercase tracking-widest">rip.</span>
            </span>
          </div>
        </div>

      </div>
    </div>
  );
};


export default ScortaCilindriModal;